import React from "react";
import { useAppDispatch, useAppSelector } from "../../Store";
import {
    socketStatusStore
} from "../../Store/Slices/socket/SocketSlice";
import useInternetConnection from "./useInternetConnection";

interface UseUserInteractionProps {
  isEnabled?: boolean; // Flag to conditionally enable or disable the hook
  timeout?: number; // Timeout for user inactivity in milliseconds
  resetDependency?: any; // Dependency that, when changed, resets the timer
}

const useUserInteraction = ({
  isEnabled = false,
  timeout = 900000,
  resetDependency,
}: UseUserInteractionProps = {}) => {
  const dispatch = useAppDispatch();

  const [userInteractionActive, setUserInteractionActive] =
    React.useState(true);
  const timeoutIdRef = React.useRef<NodeJS.Timeout | null>(null);

  const { socket } = useAppSelector(socketStatusStore);
  const { isOnline } = useInternetConnection();

  const disconnectSocketEvent = () => {
    if (!isOnline) return;

    socket?.disconnect();

    setTimeout(() => { socket.connect(); handleInteraction(); }, 500);
  };

  const resetTimer = () => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    timeoutIdRef.current = setTimeout(() => {
      setUserInteractionActive(false);
      disconnectSocketEvent();
    }, timeout);
  };

  const handleInteraction = () => {
    setUserInteractionActive(true);
    resetTimer();
  };

  React.useEffect(() => {
    if (isEnabled) {
      // Set up event listeners
      // window.addEventListener("mousemove", handleInteraction);
      // window.addEventListener("mousedown", handleInteraction);
      window.addEventListener("keydown", handleInteraction);
      window.addEventListener("click", handleInteraction);
      window.addEventListener("scroll", handleInteraction);
      // Initial setup
      resetTimer();

      // Clean up event listeners
      return () => {
        // window.removeEventListener("mousemove", handleInteraction);
        // window.removeEventListener("mousedown", handleInteraction);
        window.removeEventListener("keydown", handleInteraction);
        window.removeEventListener("click", handleInteraction);
        window.removeEventListener("scroll", handleInteraction);

        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
        }
      };
    }
  }, [timeout, resetDependency, socket?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!userInteractionActive && socket?.connected && socket?.id && isOnline) {
      handleInteraction();
    }
  }, [socket, isOnline]); // eslint-disable-line react-hooks/exhaustive-deps

  return { userInteractionActive };
};

export default useUserInteraction;
